import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import type {
  AprendizajeEsperado,
  CriterioEvaluacion,
  ObjetivoGenerico,
} from "../types";
import StatusLine from "./StatusLine";
import CriterioAccordion from "./CriterioAccordion";

interface Props {
  ae: AprendizajeEsperado;

  expanded: boolean;
  onToggle: (next: boolean) => void;
  onFetchCE: () => Promise<void>;

  criterios: CriterioEvaluacion[];
  loadingCE: boolean;
  errorCE: string;

  expandedCE: Record<string, boolean>;
  onToggleCE: (ceId: string, next: boolean) => void;

  onFetchOG: (criterioId: string) => Promise<void>;
  objetivosByCriterio: Record<string, ObjetivoGenerico[]>;
  loadingOGByCriterio: Record<string, boolean>;
  errorOGByCriterio: Record<string, string>;

  isOGSelected: (aeId: string, ceId: string, ogId: string) => boolean;
  toggleOGSelection: (aeId: string, ceId: string, ogId: string) => void;
}

const AprendizajeAccordion: React.FC<Props> = ({
  ae,
  expanded,
  onToggle,
  onFetchCE,

  criterios,
  loadingCE,
  errorCE,

  expandedCE,
  onToggleCE,

  onFetchOG,
  objetivosByCriterio,
  loadingOGByCriterio,
  errorOGByCriterio,

  isOGSelected,
  toggleOGSelection,
}) => {
  const hasCriterios = criterios.length > 0;
  const showEmpty = !loadingCE && !errorCE && !hasCriterios;

  return (
    <Accordion
      expanded={expanded}
      onChange={async (_e, next) => {
        onToggle(next);
        if (next) await onFetchCE();
      }}
      disableGutters
      sx={{
        border: "1px solid rgba(0,0,0,0.10)",
        borderRadius: "10px",
        mb: 1,
        overflow: "hidden",
        "&:before": { display: "none" },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography
          sx={{
            fontFamily: "Montserrat",
            fontSize: "0.8rem",
            fontWeight: 400,
            color: "rgba(0,0,0,0.75)",
            lineHeight: 1.25,
          }}
        >
          {ae.texto}
        </Typography>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0, pb: 1.2 }}>
        <Typography
          sx={{
            fontFamily: "Montserrat",
            fontSize: "0.78rem",
            fontWeight: 700,
            color: "rgba(0,0,0,0.60)",
            mb: 0.8,
            ml: 1,
          }}
        >
          CRITERIOS DE EVALUACIÓN
        </Typography>

        <StatusLine
          size="sm"
          loading={loadingCE}
          loadingText="Cargando criterios..."
          error={errorCE}
          empty={showEmpty}
          emptyText="No se encontraron criterios para este aprendizaje."
        />

        {!loadingCE && !errorCE && hasCriterios && (
          <Stack spacing={1}>
            {criterios.map((ce) => {
              const ceExpanded = Boolean(expandedCE[ce.id]);
              const loadingOG = Boolean(loadingOGByCriterio[ce.id]);
              const ogError = errorOGByCriterio[ce.id] ?? "";
              const ogs = objetivosByCriterio[ce.id] ?? [];

              return (
                <CriterioAccordion
                  key={ce.id}
                  aeId={ae.id}
                  ce={ce}
                  expanded={ceExpanded}
                  onToggle={(next) => onToggleCE(ce.id, next)}
                  onFetchOG={() => onFetchOG(ce.id)}
                  ogs={ogs}
                  loadingOG={loadingOG}
                  errorOG={ogError}
                  isOGSelected={isOGSelected}
                  toggleOGSelection={toggleOGSelection}
                />
              );
            })}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AprendizajeAccordion;
